import React, { createContext, useContext, useState, useCallback } from "react";
import { refreshAccessToken } from "../../services/apiClient";

// Grace period (seconds) before JWT exp to trigger a proactive refresh
const EXPIRY_GRACE_SECONDS = 30;

/** Decode the `exp` claim from a JWT without verifying the signature. */
function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

/** Returns true if the token exists AND its exp is still in the future (with grace). */
function isTokenValid(token: string | null): boolean {
  if (!token) return false;
  const exp = getTokenExpiry(token);
  if (exp === null) return false;
  return exp > Date.now() / 1000 + EXPIRY_GRACE_SECONDS;
}

interface AuthContextType {
  isAuthenticated: boolean;
  login: (_accessToken: string, _refreshToken: string) => void;
  logout: () => void;
  refresh: () => Promise<boolean>;
  token: () => string | null;
  get: (_key: string) => string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() =>
    isTokenValid(localStorage.getItem("authToken"))
  );

  const login = useCallback((accessToken: string, refreshToken: string) => {
    localStorage.setItem("authToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(async () => {
    const rawRefresh = localStorage.getItem("refreshToken");
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    setIsAuthenticated(false);
    // Best-effort server-side revocation — ignore errors
    if (rawRefresh) {
      try {
        await fetch("/api/logout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: rawRefresh }),
        });
      } catch {
        // ignore
      }
    }
  }, []);

  /**
   * Attempt a silent refresh. Returns true on success, false on failure.
   * On failure the auth state is cleared (user will be redirected by apiClient).
   */
  const refresh = useCallback(async (): Promise<boolean> => {
    // Delegate to the shared single-flight refresh so a concurrent apiFetch 401
    // and this call rotate the (server-revoked-on-use) refresh token only once.
    const newToken = await refreshAccessToken();
    if (newToken) {
      setIsAuthenticated(true);
      return true;
    }
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    setIsAuthenticated(false);
    return false;
  }, []);

  const token = useCallback(() => localStorage.getItem("authToken"), []);

  const get = useCallback((key: string) => {
    const tokenValue = localStorage.getItem("authToken");
    if (!tokenValue) return null;
    try {
      const payload = JSON.parse(atob(tokenValue.split(".")[1]));
      return payload[key] ?? null;
    } catch {
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, refresh, token, get }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
